import * as THREE from 'three';
import { G, M, TINT, glowMat, mesh, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   NICHTS 虛無 —— 最終・空洞
   ============================================================

   剪影：機甲形狀的洞・只有邊緣發光
   階段：'P1 半亮的骨架','P2 四肢展開・邊緣全亮・空洞脹大','P3 頭部裂開・露出反向核心'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-28 NICHTS 虛無 ── 機甲形狀的**空洞**。
   量體全部是 `M.void`（不受光的死黑），只有骨架邊緣在發光。
   它不是「一個黑色的東西」，是「那個位置沒有東西」——
   在一個所有物件都靠平面著色分面的世界裡，這個差別讀得出來。
   剪影是玩家認得的機甲輪廓，但裡面什麼都沒有。
   P1 半透的骨架 → P2 長出四肢 → P3 邊緣全亮、體積脹大 → P4 頭部裂開，露出反向的核心。 */
export function buildNichts(){
  var GLV = glowMat(TINT.vio.hex, 2.7), GLA = glowMat(TINT.amb.hex, 2.4);
  var root = new THREE.Group(), i, sg, legs = [], arms = [];
  var hips = boneG(root, 0, 10.40, 0);
  hips.add(mesh(G.box, M.void, [5.00, 1.90, 3.40], [0,0,0]));
  hips.add(rib(M.armorF, 5.20, 0.16, 0.16, [0, 0.95, 0.90]));
  hips.add(rib(M.armorF, 5.20, 0.16, 0.16, [0,-0.95, 0.90]));
  for (i = 0; i < 2; i++){
    sg = i ? -1 : 1;
    var th = boneG(hips, sg*2.20, -0.70, 0);
    th.add(mesh(G.box, M.void, [1.70, 5.20, 1.70], [0,-2.60,-1.10], [0.42,0,0]));
    var e1 = mesh(G.box, GLV, [0.12, 5.00, 0.12], [sg*0.85,-2.60,-0.30], [0.42,0,0]);
    e1.userData.noMerge = 1; th.add(e1);
    var shn = boneG(th, 0, -4.70, -2.20);
    shn.add(mesh(G.box, M.void, [1.40, 5.20, 1.40], [0,-2.40,1.30], [-0.46,0,0]));
    var e2 = mesh(G.box, GLA, [0.12, 5.00, 0.12], [sg*0.70,-2.40, 1.90], [-0.46,0,0]);
    e2.userData.noMerge = 1; shn.add(e2);
    var ft = boneG(shn, 0, -4.70, 2.50);
    ft.add(mesh(G.box, M.void, [2.30, 0.90, 4.20], [0,-0.30,0.50]));
    legs.push({th:th, sh:shn, ft:ft, e1:e1, e2:e2, s:sg});
  }
  var tor = boneG(hips, 0, 1.40, 0);
  tor.rotation.x = 0.14;
  tor.add(mesh(G.box, M.void, [5.40, 5.40, 3.80], [0, 2.60, 0]));
  tor.add(rib(M.armorF, 0.16, 5.20, 0.16, [ 2.60, 2.60, 1.85]));
  tor.add(rib(M.armorF, 0.16, 5.20, 0.16, [-2.60, 2.60, 1.85]));
  tor.add(rib(M.armorF, 5.20, 0.16, 0.16, [0, 5.10, 1.85]));
  tor.add(rib(M.armorF, 5.20, 0.16, 0.16, [0, 0.10, 1.85]));
  var chest = mesh(G.tor, GLV, [3.40,3.40,0.34], [0, 2.60, 1.95]);
  chest.userData.noMerge = 1; tor.add(chest);
  for (i = 0; i < 2; i++){
    sg = i ? -1 : 1;
    var sd = boneG(tor, sg*3.60, 4.40, 0);
    sd.add(mesh(G.box, M.void, [2.00, 2.20, 2.60], [0,0,0]));
    var ua = boneG(sd, 0, -1.30, 0);
    ua.add(mesh(G.box, M.void, [1.30, 3.40, 1.30], [0,-1.70,0]));
    var ea = mesh(G.box, GLA, [0.12, 3.20, 0.12], [sg*0.66,-1.70, 0.66]);
    ea.userData.noMerge = 1; ua.add(ea);
    var fa = boneG(ua, 0, -3.40, 0);
    fa.add(mesh(G.box, M.void, [1.10, 3.20, 1.10], [0,-1.60,0.10]));
    fa.add(mesh(G.cone, M.void, [1.00, 2.20, 1.00], [0,-3.60,0.10], [Math.PI,0,0]));
    arms.push({sd:sd, ua:ua, fa:fa, ea:ea, s:sg});
  }
  /* 頭：兩半，P4 裂開 */
  var hd = boneG(tor, 0, 6.60, 0.20);
  var hl = boneG(hd,  0.62, 0, 0), hr = boneG(hd, -0.62, 0, 0);
  hl.add(mesh(G.box, M.void, [1.20, 2.00, 2.30], [0,0,0]));
  hr.add(mesh(G.box, M.void, [1.20, 2.00, 2.30], [0,0,0]));
  var ey = mesh(G.oct, GLA, [1.10, 1.10, 1.10], [0, 0, 0.40]);
  ey.userData.noMerge = 1; hd.add(ey);
  var neg = mesh(G.oct, GLV, [2.40, 2.40, 2.40], [0, 0, 0]);
  neg.userData.noMerge = 1; neg.visible = false; hd.add(neg);
  root.userData.rig = {hips:hips, tor:tor, hd:hd, hl:hl, hr:hr, legs:legs, arms:arms,
                       chest:chest, ey:ey, neg:neg, GLV:GLV, GLA:GLA};
  return mergeBones(root);
}
export function updNichts(rig, o, dt){
  stages(rig, o, dt, 1.4);
  var s1 = rig.s1, s2 = rig.s2, s3 = rig.s3, i, L, A;
  for (i = 0; i < 2; i++){
    L = rig.legs[i];
    var ph = (o.ph + i*0.5) % 1, sw = Math.sin(ph*Math.PI * 2)*o.mv;
    L.th.rotation.x = sw*0.24 - 0.10*s1;
    L.sh.rotation.x = -Math.max(0, sw)*0.30 + 0.16*s1;
    L.ft.rotation.x = 0.10*s1;
    L.e1.scale.setScalar(0.3 + 0.9*s1 + 0.5*s2);
    L.e2.scale.setScalar(0.3 + 0.9*s1 + 0.5*s2);
    A = rig.arms[i];
    A.sd.rotation.z = A.s*(0.10 + 0.40*s1);
    A.ua.rotation.x = 0.20 + 0.90*s1 - o.tel*0.50 + o.atk*1.00;
    A.fa.rotation.x = -0.30 - 0.50*s1 + o.atk*0.34;
    A.ea.scale.setScalar(0.3 + 0.9*s1 + 0.5*s2);
  }
  rig.hips.position.y = 10.40 + 0.24*Math.sin(o.ph*2*Math.PI * 2)*o.mv + 0.9*s1;
  rig.hips.rotation.x = 0.05*o.mv - o.tel*0.10;
  rig.tor.rotation.x = 0.14 - o.tel*0.14 + o.atk*0.20;
  rig.tor.rotation.y = 0.26*Math.sin(o.t*0.7);
  /* 體積脹大：空洞本身變大，但邊框不變粗 —— 讀作「洞在擴」 */
  rig.tor.scale.setScalar(1 + 0.30*s1 + 0.12*s2);
  rig.hips.scale.setScalar(1 + 0.18*s1);
  /* P4 頭裂開，露出反向的核心 */
  rig.hl.position.x =  0.62 + 1.30*s2;
  rig.hr.position.x = -0.62 - 1.30*s2;
  rig.hl.rotation.z = -0.30*s2; rig.hr.rotation.z = 0.30*s2;
  rig.neg.visible = s2 > 0.25;
  rig.neg.scale.setScalar(s2*(1 + 0.18*Math.sin(o.t*3.4) + 0.9*o.tel));
  rig.ey.scale.setScalar((1 - 0.6*s2)*(1 + 0.16*Math.sin(o.t*2.4) + 1.0*o.tel));
  rig.chest.scale.set(3.40*(1 + 0.5*s1 + 0.30*s2), 3.40*(1 + 0.5*s1 + 0.30*s2), 0.34);
  rig.chest.rotation.z += dt*(0.5 + 2.4*s2);
  rig.GLV.emissiveIntensity = 1.0 + 2.6*s1 + 3.0*s2 + 2.8*o.tel;
  rig.GLA.emissiveIntensity = 1.2 + 2.2*s1 + 2.6*s2 + 3.0*o.atk;
}